import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCheck, FolderTree, Loader2, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { serverMessage } from '~/lib/api/server-error';
import { CategoryTree } from './category-tree';
import {
	useMapCategories,
	useMappableAttributes,
	useAssignedAttributes,
	useSaveCategoryAttributes
} from '~/hooks/use-category-attributes-map';

/** Category → Attributes mapping (legacy "Απόδοση σε Κατηγορίες"): pick a category, toggle its attributes. */
export function CategoryAttributesMapView() {
	const { t } = useTranslation();
	const categories = useMapCategories();
	const attributes = useMappableAttributes();

	const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
	const [query, setQuery] = useState('');
	const [checked, setChecked] = useState<Set<number>>(new Set());

	const assigned = useAssignedAttributes(selected?.id);
	const save = useSaveCategoryAttributes();

	// Re-seed the local checked set whenever the loaded assignment for the selected category arrives.
	useEffect(() => {
		if (assigned.data) setChecked(new Set(assigned.data));
	}, [assigned.data]);

	const filteredAttributes = useMemo(() => {
		const q = query.trim().toLowerCase();
		const list = attributes.data ?? [];
		return q ? list.filter(a => a.name.toLowerCase().includes(q)) : list;
	}, [attributes.data, query]);

	// Optimistically apply the next set, persist it, and revert on failure.
	const persist = (categoryId: number, nextSet: Set<number>) => {
		const prev = checked;
		setChecked(nextSet);
		save.mutate(
			{ categoryId, attributeIds: [...nextSet] },
			{
				onError: err => {
					setChecked(prev);
					toast.error(serverMessage(err));
				}
			}
		);
	};

	const onToggle = (attributeId: number, next: boolean) => {
		if (!selected) return;
		const nextSet = new Set(checked);
		next ? nextSet.add(attributeId) : nextSet.delete(attributeId);
		persist(selected.id, nextSet);
	};

	const onSelectAll = () => selected && persist(selected.id, new Set((attributes.data ?? []).map(a => a.id)));
	const onClearAll = () => selected && persist(selected.id, new Set());

	const allAttributeCount = attributes.data?.length ?? 0;
	const assignedCount = checked.size;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('attributes.map.title')}</h1>
					<p className="mt-0.5 text-sm text-foreground-muted">{t('attributes.map.subtitle')}</p>
				</div>
			</div>

			<div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
				{/* Left: category tree */}
				<div className="flex min-h-[28rem] flex-col">
					<h2 className="mb-2 text-xs font-semibold tracking-[0.04em] text-foreground-subtle uppercase">
						{t('attributes.map.categories')}
					</h2>
					<div className="min-h-0 flex-1">
						<CategoryTree
							categories={categories.data ?? []}
							loading={categories.isLoading}
							selectedId={selected?.id ?? null}
							onSelect={(id, name) => setSelected({ id, name })}
						/>
					</div>
				</div>

				{/* Right: attribute checklist for the selected category */}
				<div className="flex min-h-[28rem] flex-col">
					<h2 className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-[0.04em] text-foreground-subtle uppercase">
						{t('attributes.map.attributes')}
						{save.isPending && <Loader2 size={13} className="animate-spin text-foreground-muted" />}
					</h2>
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-surface">
						{!selected ? (
							<div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
								<FolderTree size={36} className="text-foreground-disabled" strokeWidth={1.25} />
								<p className="text-sm text-foreground-muted">{t('attributes.map.selectCategory')}</p>
							</div>
						) : (
							<>
								<div className="border-b border-border-subtle px-4 py-3">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<p className="truncate text-sm font-medium text-foreground">{selected.name}</p>
											<p className="text-xs text-foreground-muted">{t('attributes.map.assignedCount', { count: assignedCount })}</p>
										</div>
										<div className="flex shrink-0 items-center gap-1">
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={onSelectAll}
												disabled={save.isPending || assignedCount === allAttributeCount}>
												<CheckCheck size={14} /> {t('attributes.map.selectAll')}
											</Button>
											<Button type="button" variant="ghost" size="sm" onClick={onClearAll} disabled={save.isPending || assignedCount === 0}>
												<X size={14} /> {t('attributes.map.clearAll')}
											</Button>
										</div>
									</div>
									<div className="relative mt-3">
										<Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-foreground-subtle" />
										<Input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('common.search')} className="pl-9" />
									</div>
								</div>
								<div className="min-h-0 flex-1 overflow-y-auto">
									{attributes.isLoading || assigned.isLoading ? (
										<div className="flex flex-col gap-2 p-4">
											{Array.from({ length: 6 }).map((_, i) => (
												<Skeleton key={i} className="h-6 w-full" />
											))}
										</div>
									) : filteredAttributes.length === 0 ? (
										<p className="px-4 py-6 text-center text-sm text-foreground-muted">{t('common.noResults')}</p>
									) : (
										<div className="flex flex-col">
											{filteredAttributes.map(a => (
												<div
													key={a.id}
													className="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-2.5 last:border-b-0 hover:bg-elevated">
													<Label
														htmlFor={`map-attr-${a.id}`}
														className="min-w-0 flex-1 cursor-pointer truncate text-sm font-normal text-foreground">
														{a.name}
													</Label>
													<Switch id={`map-attr-${a.id}`} checked={checked.has(a.id)} onCheckedChange={next => onToggle(a.id, next)} />
												</div>
											))}
										</div>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
